import { useDisclosure } from '@mantine/hooks'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { datasetApi } from '@/api/datasetApi'
import { getApiError } from '@/api/helpers/apiError'
import { useApplicationContext } from '@/providers/ApplicationContext'
import { useModelContext } from '@/providers/ModelContext'
import { useInvalidateDatasetQueries } from '@/queries/datasetQueries'
import { useUpdateModel } from '@/queries/modelQueries'
import { showToast } from '@/theme/notifications'
import { Dataset, DatasetType, LabelingType } from '@/types/dataset'
import { MLModelType } from '@/types/model'
import { PageWithFixedToolbar } from '../layout/PageWithFixedToolbar/PageWithFixedToolbar'
import { useModelStepNavigation } from '../models/ModelDetails/useModelStepNavigation'
import { PlatformAlert } from '../ui-shared/PlatformAlert/PlatformAlert'
import {
  CreateDatasetHandler,
  DatasetCreationResult
} from './CreateDatasetHandler/CreateDatasetHandler'
import { CreateDatasetModal } from './CreateDatasetModal/CreateDatasetModal'
import { DatasetListToolbar } from './DatasetListToolbar/DatasetListToolbar'
import { DatasetPreview } from './DatasetPreview/DatasetPreview'
import { SelectableDatasetList } from './SelectableDatasetList/SelectableDatasetList'

export const SelectDatasetPage = () => {
  const intl = useIntl()

  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null)
  const [previewDataset, setPreviewDataset] = useState<Dataset | null>(null)

  const [isPending, setIsPending] = useState(false)

  const [
    isCreateModalOpened,
    { open: openCreateModal, close: closeCreateModal }
  ] = useDisclosure(false)

  const { application } = useApplicationContext()
  const { model } = useModelContext()

  const { goToLabelDataScreen } = useModelStepNavigation({
    appId: application?.id || '',
    modelId: model?.id || ''
  })

  const { mutateAsync: updateModel } = useUpdateModel(model?.id || '')

  const { invalidateDatasetInfiniteList } = useInvalidateDatasetQueries()

  const initialDatasetId = model?.dataset_version?.dataset || ''

  const assignDatasetAndNavigate = async (
    dataset: Dataset,
    openCrop?: boolean
  ) => {
    setIsPending(true)

    try {
      // Get the most recent dataset version and assign it to the model
      const data = await datasetApi.getDatasetVersionsList(dataset.id, {
        limit: 1,
        ordering: '-created_at'
      })

      const latestVersion = data.results[0]?.id

      if (!latestVersion) {
        return
      }

      await updateModel({
        modelId: model?.id || '',
        data: {
          dataset_version_id: latestVersion
        }
      })

      goToLabelDataScreen(false, {
        openCrop
      })
    } catch (err) {
      const { errorMessage } = getApiError(err)

      const message =
        errorMessage || intl.formatMessage({ id: 'datasets.assign.error' })

      showToast(message, 'error')
    }

    setIsPending(false)
  }

  const handleOnContinue = () => {
    if (!selectedDataset) {
      return
    }

    void assignDatasetAndNavigate(selectedDataset)
  }

  const handleDatasetCreated = (result: DatasetCreationResult) => {
    closeCreateModal()

    const { dataset } = result

    // Check if dataset and model types match. If not, refresh the list to show the new dataset as disabled.
    const isModelDatasetTypeMismatch =
      (model?.model_type === MLModelType.Primary &&
        dataset.dataset_type === DatasetType.Secondary) ||
      (model?.model_type === MLModelType.Secondary &&
        dataset.dataset_type === DatasetType.Primary)

    const isLabelingTypeMismatch =
      model?.labeling_type !== dataset.labeling_type

    if (isModelDatasetTypeMismatch || isLabelingTypeMismatch) {
      void invalidateDatasetInfiniteList()
    } else {
      void assignDatasetAndNavigate(
        dataset,
        dataset.dataset_type === DatasetType.Secondary
      )
    }
  }

  return (
    <PageWithFixedToolbar
      toolbar={
        <DatasetListToolbar
          isDatasetSelected={!!selectedDataset}
          name={selectedDataset?.name || ''}
          isPending={isPending}
          onContinue={handleOnContinue}
        />
      }
    >
      <PlatformAlert
        mb="xl"
        title={<FormattedMessage id="datasets.select.banner.title" />}
        variant="info"
      >
        <FormattedMessage id="datasets.select.banner.description" />
      </PlatformAlert>

      <SelectableDatasetList
        initialDatasetId={initialDatasetId}
        selectedDatasetId={selectedDataset?.id || ''}
        selectableDatasetType={
          model?.model_type === MLModelType.Primary
            ? DatasetType.Primary
            : DatasetType.Secondary
        }
        selectableLabelingType={
          model?.labeling_type || LabelingType.ObjectDetection
        }
        onPreviewDataset={setPreviewDataset}
        onSelectDataset={setSelectedDataset}
        onCreateDataset={openCreateModal}
        onResetSelection={() => setSelectedDataset(null)}
      />

      <CreateDatasetModal
        opened={isCreateModalOpened}
        onClose={closeCreateModal}
      >
        <CreateDatasetHandler
          initialValues={{
            datasetType:
              model?.model_type === MLModelType.Primary
                ? DatasetType.Primary
                : DatasetType.Secondary,
            labelingType: model?.labeling_type || LabelingType.ObjectDetection
          }}
          onCancel={closeCreateModal}
          onDatasetCreated={handleDatasetCreated}
        />
      </CreateDatasetModal>

      <DatasetPreview
        datasetId={previewDataset?.id}
        opened={!!previewDataset}
        onClose={() => setPreviewDataset(null)}
      />
    </PageWithFixedToolbar>
  )
}
